/**
 * ScansPage — CRM / Scan Notifications
 *
 * Features:
 * - List filter tabs (all, new, hot, warm, cold, friends)
 * - Scan contact cards with Note + Move buttons inline
 * - Tap a person's NAME → opens full profile detail modal
 * - Notes per contact
 * - Move contacts between lists
 * - Save contact as .vcf from detail view
 *
 * API_TODO:
 * - GET /api/users/me/scans?list=<filter> → paginated scan list
 * - PUT /api/scans/:scanId { notes, list } → update scan
 * - GET /api/scans/:scanId/vcard → download scanner's .vcf
 * - WebSocket: SCAN_EVENT → real-time scan notification push
 */
import { useState } from 'react';
import { Icons } from '../components/Icons';
import AscendingK from '../components/AscendingK';
import { getInitials } from '../utils/validation';
import { apiRequest } from '../lib/api';

export default function ScansPage({ user, setUser }) {
  const [activeList, setActiveList] = useState('all');
  const [selectedScan, setSelectedScan] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [moveTarget, setMoveTarget] = useState('');
  const [editingScan, setEditingScan] = useState(null); // for inline note/move modal
  const [profileScan, setProfileScan] = useState(null); // for profile detail modal
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // ── Dynamic designation grouping ──
  // Maps title keywords to seniority categories
  const classifyRole = (title) => {
    if (!title) return 'Other';
    const t = title.toLowerCase();
    // C-Suite
    if (/\b(ceo|cto|cfo|coo|cmo|cio|ciso|cpo|cro|chief)\b/.test(t)) return 'C-Suite';
    // VP
    if (/\b(vp|vice\s*president)\b/.test(t)) return 'VP';
    // Directors / Heads
    if (/\b(director|head\s+of|svp|senior\s+vice)\b/.test(t)) return 'Directors / Heads';
    // Partners (VC, law, consulting)
    if (/\b(partner|managing\s+director|general\s+partner|principal)\b/.test(t)) return 'Partners';
    // Managers
    if (/\b(manager|lead|team\s+lead|supervisor)\b/.test(t)) return 'Managers';
    // Founders
    if (/\b(founder|co-founder|cofounder|owner)\b/.test(t)) return 'Founders';
    return 'Other';
  };

  // Build available role categories from actual scans (dynamic)
  const roleCategoryCounts = {};
  user.scans.forEach((s) => {
    const cat = classifyRole(s.title);
    roleCategoryCounts[cat] = (roleCategoryCounts[cat] || 0) + 1;
  });
  // Only show categories that have scans, sorted by count descending
  const availableRoleFilters = Object.entries(roleCategoryCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, count]) => ({ key: cat, label: cat, count }));

  // Filter pipeline: list → role → search
  const listFiltered = activeList === 'all' ? user.scans : user.scans.filter((s) => s.list === activeList);
  const roleFiltered = roleFilter === 'all' ? listFiltered : listFiltered.filter((s) => classifyRole(s.title) === roleFilter);
  const filteredScans = searchQuery.trim()
    ? roleFiltered.filter((s) => {
        const q = searchQuery.toLowerCase();
        return (
          (s.name && s.name.toLowerCase().includes(q)) ||
          (s.company && s.company.toLowerCase().includes(q)) ||
          (s.title && s.title.toLowerCase().includes(q)) ||
          (s.email && s.email.toLowerCase().includes(q)) ||
          (s.notes && s.notes.toLowerCase().includes(q))
        );
      })
    : roleFiltered;

  const listCounts = {};
  user.scans.forEach((s) => {
    listCounts[s.list] = (listCounts[s.list] || 0) + 1;
  });

  const updateScanNote = async (scanId, note, list) => {
    await apiRequest(`/scans/${scanId}`, {
      method: 'PUT',
      body: { notes: note, list }
    });
    setUser((prev) => ({
      ...prev,
      scans: prev.scans.map((s) => (s.id === scanId ? { ...s, notes: note, list } : s)),
    }));
  };

  const moveScan = (scanId, targetList) => {
    setUser((prev) => ({
      ...prev,
      scans: prev.scans.map((s) => (s.id === scanId ? { ...s, list: targetList } : s)),
    }));
    setEditingScan(null);
  };

  const handleSaveContact = (scan) => {
    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${scan.name}`,
      `TITLE:${scan.title || ''}`,
      `ORG:${scan.company || ''}`,
      `EMAIL:${scan.email || ''}`,
    ];
    if (scan.phone) lines.push(`TEL:${scan.phone}`);
    if (scan.links) {
      scan.links.forEach((l) => lines.push(`URL:${l.url}`));
    }
    lines.push('END:VCARD');
    const blob = new Blob([lines.join('\n')], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scan.name.replace(/\s+/g, '_')}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEditModal = (scan) => {
    setEditingScan(scan);
    setNoteText(scan.notes || '');
    setMoveTarget(scan.list);
  };

  const openProfileModal = (scan, e) => {
    e.stopPropagation();
    setProfileScan(scan);
  };

  return (
    <>
      <div className="page-header">
        <div className="mono" style={{ marginBottom: 8 }}>Scan Notifications</div>
        <h1 className="page-title">
          Your <em>network</em>.
        </h1>
        <p className="page-subtitle">{user.scans.length} people have scanned your card.</p>
      </div>

      <div className="page-body">
        {/* Search + role filter bar */}
        <div className="scan-search-bar">
          <div className="scan-search-input-wrap">
            <svg className="scan-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              type="text"
              className="scan-search-input"
              placeholder="Search by name, company, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="scan-search-clear" onClick={() => setSearchQuery('')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          <div className="scan-role-filter-wrap">
            <select
              className="scan-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              {availableRoleFilters.map((rf) => (
                <option key={rf.key} value={rf.key}>
                  {rf.label} ({rf.count})
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Active filters summary */}
        {(searchQuery.trim() || roleFilter !== 'all') && (
          <div className="scan-active-filters">
            <span className="scan-filter-count">{filteredScans.length} result{filteredScans.length !== 1 ? 's' : ''}</span>
            {roleFilter !== 'all' && (
              <button className="scan-filter-chip" onClick={() => setRoleFilter('all')}>
                {roleFilter} <span style={{ marginLeft: 4, opacity: 0.6 }}>&times;</span>
              </button>
            )}
            {searchQuery.trim() && (
              <button className="scan-filter-chip" onClick={() => setSearchQuery('')}>
                "{searchQuery}" <span style={{ marginLeft: 4, opacity: 0.6 }}>&times;</span>
              </button>
            )}
          </div>
        )}

        {/* Filter tabs */}
        <div className="list-tabs">
          {[{ key: 'all', label: 'All' }, ...user.lists.map((l) => ({ key: l, label: l }))].map((tab) => (
            <button
              key={tab.key}
              className={`list-tab ${activeList === tab.key ? 'active' : ''}`}
              onClick={() => setActiveList(tab.key)}
            >
              {tab.label}
              <span className="tab-count">{tab.key === 'all' ? user.scans.length : listCounts[tab.key] || 0}</span>
            </button>
          ))}
        </div>

        {/* Scan items — Note + Move inline, name is clickable for profile */}
        {filteredScans.map((scan, i) => (
          <div className="scan-item" key={scan.id} style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="scan-item-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="scan-item-avatar">
                  <span className="scan-item-initials">{getInitials(scan.name)}</span>
                </div>
                <div>
                  <div
                    className="scan-item-name scan-item-name-link"
                    onClick={(e) => openProfileModal(scan, e)}
                  >
                    {scan.name}
                  </div>
                  <div className="scan-item-title">
                    {scan.title}{scan.company ? ` · ${scan.company}` : ''}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="scan-item-time">{scan.time}</div>
                <span className={`list-badge ${scan.list}`} style={{ marginTop: 4, display: 'inline-block' }}>
                  {scan.list}
                </span>
              </div>
            </div>
            <div className="scan-item-email">{scan.email}</div>
            {scan.notes && <div className="scan-item-notes">{scan.notes}</div>}
            <div className="scan-item-actions">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => openEditModal(scan)}
              >
                {Icons.note} Note
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => openEditModal(scan)}
              >
                {Icons.move} Move
              </button>
            </div>
          </div>
        ))}

        {filteredScans.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>
            {searchQuery.trim() ? (
              <>
                <p className="serif" style={{ fontSize: 20, fontStyle: 'italic', marginBottom: 8 }}>
                  No matches for "{searchQuery}"
                </p>
                <p style={{ fontSize: 14 }}>Try a different name, company, or keyword.</p>
              </>
            ) : (
              <>
                <p className="serif" style={{ fontSize: 20, fontStyle: 'italic', marginBottom: 8 }}>
                  No scans here yet.
                </p>
                <p style={{ fontSize: 14 }}>Share your card at your next event to start building your network.</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Inline note/move modal (same as original) ── */}
      {editingScan && (
        <div className="modal-overlay" onClick={() => setEditingScan(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editingScan.name}</span>
              <button className="btn btn-ghost-app" onClick={() => setEditingScan(null)}>
                {Icons.close}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input form-textarea"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note about this person..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Move to list</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {user.lists.map((list) => (
                    <button
                      key={list}
                      className={`btn btn-sm ${moveTarget === list ? 'btn-primary-app' : 'btn-outline'}`}
                      onClick={() => setMoveTarget(list)}
                    >
                      {list}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost-app btn-sm" onClick={() => setEditingScan(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary-app btn-sm"
                onClick={() => {
                  updateScanNote(editingScan.id, noteText, moveTarget);
                  if (moveTarget !== editingScan.list) {
                    moveScan(editingScan.id, moveTarget);
                  } else {
                    setEditingScan(null);
                  }
                }}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile detail modal (opens when tapping name) ── */}
      {profileScan && (
        <div className="modal-overlay" onClick={() => setProfileScan(null)}>
          <div className="scan-detail-modal" onClick={(e) => e.stopPropagation()}>
            {/* Close button — clearly visible top-right */}
            <button className="scan-detail-close" onClick={() => setProfileScan(null)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>

            {/* Profile card */}
            <div className="scan-detail-card">
              <div className="scan-header">
                <div className="scan-avatar">
                  <span className="initials">{getInitials(profileScan.name)}</span>
                </div>
                <div className="scan-user-info">
                  <div className="scan-name">{profileScan.name}</div>
                  <div className="scan-role">
                    {profileScan.title}
                    {profileScan.company ? ` · ${profileScan.company}` : ''}
                  </div>
                </div>
              </div>

              {profileScan.about && (
                <div className="scan-about">{profileScan.about}</div>
              )}

              <div className="scan-actions">
                <button className="scan-action-primary" onClick={() => handleSaveContact(profileScan)}>
                  {Icons.save} Save contact
                </button>
              </div>

              <div className="scan-secondary">
                {profileScan.phone && (
                  <a href={`tel:${profileScan.phone}`} className="scan-btn-sec" style={{ textDecoration: 'none' }}>
                    <span className="sc-label">Call</span>
                    <span className="sc-value">{profileScan.phone}</span>
                  </a>
                )}
                {profileScan.email && (
                  <a href={`mailto:${profileScan.email}`} className="scan-btn-sec" style={{ textDecoration: 'none' }}>
                    <span className="sc-label">Email</span>
                    <span className="sc-value">{profileScan.email}</span>
                  </a>
                )}
              </div>

              {profileScan.links && profileScan.links.length > 0 && (
                <div className="scan-links">
                  {profileScan.links.map((link, idx) => (
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="scan-link-row"
                      key={idx}
                      style={{ textDecoration: 'none' }}
                    >
                      <span className="scan-link-label">{link.platform}</span>
                      <span className="scan-link-value">Open &rarr;</span>
                    </a>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0 0', borderTop: '1px solid var(--rule)', marginTop: 16 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Scanned {profileScan.time}</span>
                <span className={`list-badge ${profileScan.list}`}>{profileScan.list}</span>
              </div>
            </div>

            <div className="scan-footer" style={{ marginTop: 16 }}>
              <AscendingK size={12} color="#B8B2A2" />
              <span className="scan-footer-text">Powered by Connekted in</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
